import { marked } from 'marked'

// Pre-load all career markdown files using Vite's import.meta.glob with eager loading
// This allows both getAllJobs and getJobBySlug to use the same static imports
const jobModules = import.meta.glob('/src/content/careers/*.md', { 
  eager: true,
  query: '?raw',
  import: 'default'
})

/**
 * Get all job listings from the careers directory
 * @returns {Promise<Array>} Array of job objects with metadata
 */
export async function getAllJobs() {
  try {
    const jobs = []
    
    for (const path in jobModules) {
      const content = jobModules[path]
      const filename = path.split('/').pop().replace('.md', '')
      const slug = filename
      
      // Parse frontmatter and content
      const { metadata, content: markdownContent } = parseMarkdown(content)
      
      jobs.push({
        slug,
        filename,
        ...metadata,
        excerpt: markdownContent.substring(0, 200) + '...'
      })
    }
    
    // Sort by date (newest first) or by title
    return jobs.sort((a, b) => {
      if (a.date && b.date) {
        return new Date(b.date) - new Date(a.date)
      }
      return a.title?.localeCompare(b.title) || 0
    })
  } catch (error) {
    console.error('Error loading jobs:', error)
    return []
  }
}

/**
 * Get a single job by slug
 * @param {string} slug - The job slug
 * @returns {Promise<Object|null>} Job object with parsed markdown content
 */
export async function getJobBySlug(slug) {
  try {
    // Find the matching path from pre-loaded modules
    const matchingPath = Object.keys(jobModules).find(path => 
      path.endsWith(`/${slug}.md`)
    )
    
    if (!matchingPath) {
      return null
    }
    
    const content = jobModules[matchingPath]
    
    if (!content) {
      return null
    }
    
    const { metadata, content: markdownContent } = parseMarkdown(content)
    const htmlContent = marked(markdownContent)
    
    return {
      slug,
      ...metadata,
      content: htmlContent,
      rawContent: markdownContent
    }
  } catch (error) {
    console.error('Error loading job:', error)
    return null
  }
}

/**
 * Parse markdown file with frontmatter
 * @param {string} content - Raw markdown content
 * @returns {Object} Object with metadata and content
 */
function parseMarkdown(content) {
  const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/
  const match = content.match(frontmatterRegex)
  
  if (!match) {
    // No frontmatter, return content as-is
    return {
      metadata: {},
      content: content.trim()
    }
  }
  
  const frontmatter = match[1]
  const markdownContent = match[2]
  
  // Parse YAML-like frontmatter (simple key-value pairs)
  const metadata = {}
  const lines = frontmatter.split('\n')
  
  for (const line of lines) {
    const colonIndex = line.indexOf(':')
    if (colonIndex > 0) {
      const key = line.substring(0, colonIndex).trim()
      let value = line.substring(colonIndex + 1).trim()
      
      // Remove quotes if present
      if ((value.startsWith('"') && value.endsWith('"')) ||
          (value.startsWith("'") && value.endsWith("'"))) {
        value = value.slice(1, -1)
      }
      
      metadata[key] = value
    }
  }
  
  return {
    metadata,
    content: markdownContent.trim()
  }
}

